import { BookOpen, Smartphone } from 'lucide-react';
import { getSafeImage } from './utils/imageLoader';

export const books = [
  {
    id: 'b1',
    title: 'Quigley Raccoon and the Backpack of Wonders',
    author: 'Willow Wise',
    description: 'An enchanting tale of Quigley the Raccoon as he navigates the magical forest, making friends and discovering new adventures.',
    tags: ['Children\'s', 'Illustrated', 'Animals'],
    color: 'bg-emerald-900',
    image: getSafeImage('QuigleyRaccoonFront Cover.jpg'),
    pages: 126,
    readingTime: '~5 mins',
    pubDate: 'November 2024',
    chapter1Title: 'Chapter 1: The Raccoon Who Kept Everything',
    chapter1Preview: `If you were to walk into the heart of the Whispering Woods, past the twisty roots of the Great Oak and just to the left of the bubbling brook, you would find a hollow log. From the outside, it looked like any other log—covered in moss, slightly damp, and home to a few friendly beetles. But if you were to peek inside (and wipe your feet on the tiny welcome mat made of woven grass), you would find it was not a log at all. It was a home.

And not just any home. It was a museum. A workshop. A treasury. This was the home of Quigley Raccoon.

Quigley was not like the other animals in the Whispering Woods. Most raccoons spent their days washing their food in the stream, napping in sunbeams, or arguing over who got the best part of a leftover apple core. Quigley did those things too, of course—he loved a good apple core—but his true passion was keeping things. While other animals saw trash, Quigley saw potential.

On this particular Tuesday morning, the sunlight was filtering through the cracks in the log, illuminating the dust that danced in the air like tiny, golden fairies. Quigley sat in his favourite armchair, which he had constructed himself out of a discarded woven basket and stuffed with cattail fluff. It was arguably the most comfortable chair in the entire forest, though it did squeak alarmingly if he sat down too fast.

Quigley was busy. He was inspecting his latest find: a brass button.

He held it up to his eye, squinting. He polished it on his patchwork vest—a garment he had sewn together from scraps of fabric found near the humans’ picnic grounds. The vest had six pockets on the front and two secret ones on the inside.

“Fascinating,” Quigley murmured to himself, his whiskers twitching. “Truly fascinating. It has four holes, not two. And look at the rim! It’s dented. I wonder what happened to the coat it belonged to? Perhaps it was a coat worn by a giant. Or a pirate! Yes, definitely a pirate.”

He stood up and waddled over to his Workbench of Wonders. To an untrained eye, the workbench looked like a disaster. There were piles of string, mounds of bottle caps, a stack of broken spectacles, three left shoes (all different sizes), and a collection of spoons bent into shapes that spoons were never meant to be.

But Quigley knew exactly where everything was. He placed the brass button into a glass jar labelled “Things That Are Round and Shiny.”

“There,” he said with satisfaction. “You shall live next to the marble with the chip in it.”

[IMAGE: quigley_jars]

Quigley turned to his most prized possession: The Backpack. It hung on a hook by the door—bright yellow and bulging at the seams. It was a heavy, canvas thing, sturdy and reliable. It had patches sewn over holes—a red patch shaped like a star, a blue patch shaped like a cloud—and the zippers jingled like sleigh bells when he walked.

“Time to pack for the day,” Quigley announced to the empty room.

He began his morning ritual. He didn't just throw things in; he selected them with the care of a chef choosing spices.

“Let’s see,” he muttered, his paws nimble and quick. “I shall need the Coil of Copper Wire. One never knows when one needs to conduct electricity, or perhaps tie up a very small bundle of sticks.” Into the bag it went.

“And the Rubber Chicken.” He gave it a squeeze. SQUUU-AAAAWK! The sound echoed through the hollow log. Quigley smiled. “Excellent acoustics. You definitely come along. Essential for morale.”

[IMAGE: quigley_backpack]

He packed a half-eaten comic book (the ending was missing, which Quigley preferred because it meant he could invent his own), a jar of pickles that he had been saving for a special occasion, and a large, silver serving spoon that was slightly tarnished.

“And finally,” Quigley said, picking up a spool of red yarn. “You. You are the most important of all. A string connects things. And connection is the key to the universe.”

He zipped the bag shut. ZZZZZ-ZIP.

He stepped out of his log and took a deep breath of the morning air. It smelled of pine needles, damp earth, and…

WHAM!

Something struck Quigley with the force of a furry cannonball.

Quigley didn't fall over—he was used to this—but his knees buckled, and his backpack gave a loud CLANK-SQUEAK.

“Morning-Quigley-Morning-Quigley-Morning-Quigley!”

The words were coming out so fast they sounded like one giant, long word. Quigley reached up and peeled the attacker off his face.

It was Pip.

Pip was a red squirrel, but that description didn't quite do him justice. Pip was a vibration wrapped in fur. His tail twitched at a speed that made it look blurry. His eyes were wide and wild, forever scanning the horizon for nuts, predators, or interesting shaped leaves.

“Good morning, Pip,” Quigley said, dusting off his vest. “You’re energetic today.”

“Energetic? I’m not energetic! I’m efficient!” Pip zoomed a circle around Quigley, running up the side of the log and back down again. “I’ve already buried twelve nuts, dug up three of them because I forgot where I put them, chased a blue jay, and had a staring contest with a beetle. The beetle won. He was very focused. What are we doing? Where are we going? Is that the backpack? Why is it so big? Can I ride in it?”

“No riding in the backpack,” Quigley said gently. “It is full of Very Important Things.”

Pip stopped moving for exactly one second, hovering on his hind legs. “Junk. It’s full of junk, Quigley. I saw you put a broken clock in there yesterday. A clock! That doesn't tick! It’s just a circle with numbers!”

“It is correct twice a day,” Quigley corrected him. “Which is more than can be said for Badger, who is never correct.”

“Touché,” Pip grinned. “So? What’s the plan? Adventure? Danger? Snacks?”

“Scavenging,” Quigley said, adjusting his straps. “I have a feeling about the North Ridge today. The wind was blowing from the humans' campground last night. There might be sandwich crusts. Or tin foil.”

“Tin foil!” Pip gasped. “The shiny paper! Oh, I love the shiny paper. Let’s go! Race you!”

Pip took off like a rocket. Quigley chuckled, shook his head, and began his steady, waddling walk behind him. The backpack clinked rhythmically—clink-clank, clink-clank—a song of preparedness.

The woods were alive. Sunlight dappled the forest floor. A woodpecker hammered a rhythm on a distant tree. It was a perfect, beautiful day in the Whispering Woods.

But as Quigley walked, a small frown touched his face. He stopped. He stood on his hind legs, his nose twitching.

Sniff. Sniff. Sniff.

The air usually smelled crisp and green. But today, underneath the scent of pine and soil, there was something else. Something… stale. Like water that had been sitting in a bucket for a week. Like old, wet wool.

“Quigley! Hurry up! You’re slow as a snail on a sticky day!” Pip yelled from a branch high above.

“Coming, Pip,” Quigley called back. But he looked over his shoulder one last time toward the south. The shadows between the trees seemed a little darker than usual. A little flatter.

He shook it off. It was probably just the damp ground. He adjusted his heavy pack and marched on, unaware that the weight on his back was about to become the weight of the world.`
  },
  {
    id: 'b2',
    title: 'A Boy called Stinky',
    author: 'Cian Zaki',
    description: 'Meet Stanley Stott, a boy with a very tidy appearance but an impossibly strong smell. When the class hamster goes missing, Stanley discovers his stinky curse might actually be a super-powered lie detector.',
    tags: ['Children\'s', 'Comedy', 'Adventure'],
    color: 'bg-indigo-950',
    image: getSafeImage('ABoyCalledStinkyFrontCover.png'),
    pages: 43,
    readingTime: '~2 mins',
    pubDate: 'February 2025',
    chapter1Title: 'Chapter 1: The Impossible Smile',
    chapter1Preview: `Tuesday was School Picture Day. For most kids, this meant combing their hair and remembering not to spill toothpaste on their shirts.

For Stanley Stott, it was a military operation.

Stanley stood in front of his bathroom mirror. He looked perfect. His white shirt was crisp. His red tie was straight. His hair was parted neatly to the side. If you looked at a photograph of Stanley, you would think, “What a tidy young man.”

But photographs don’t have a smell.

Stanley leaned in close to the mirror. "Please," he whispered to his reflection. "Just for one hour. Let me smell like soap. Or mint. Or even nothing. I would love to smell like nothing."

[IMAGE: stinky_mirror]

He sniffed his sleeve. He groaned.

Despite three showers and a whole bottle of shampoo, he did not smell like mint. He smelled, distinctly and powerfully, of the Stinking Bishop Blue Cheese stored in the cellar directly beneath his bedroom.

"Stanley! Breakfast!" his mom called from downstairs.

"Coming!" Stanley yelled back.

He took a deep breath, grabbed his backpack, and opened the bathroom door. He had a plan. If he held his breath, walked very fast, and didn't hug anyone, maybe—just maybe—he could make it through Picture Day without earning his nickname.

But as soon as he stepped onto the school bus, he knew the plan had failed.

The bus driver, Mr. Henderson, sniffed the air. He looked at his own sandwich with suspicion. Then he looked at Stanley.

"Morning, Stanley," Mr. Henderson said, opening a window even though it was freezing outside.

"Morning," Stanley sighed.

He walked down the aisle. It was like magic. As he passed the rows of seats, heads turned away. Conversations stopped. He sat in his usual spot—the empty seat at the very back, where the heater vent blew the air away from the other kids.

Being "The Smelly Kid" wasn't just about the teasing. It was the loneliness. It was like being on an island, right in the middle of a crowded bus.`
  },
  {
    id: 'b3',
    title: 'Sam and the Shadow Gremlins',
    author: 'Cian Zaki',
    description: 'Sam is a ten-year-old boy whose worries take physical shape as dark, inky shadow-creatures—from buzzing whisper-gnats to a heavy, cold Spotlight Blob. When his mom signs him up for the school talent show, Sam must learn a special focusing technique to shrink his fears down to pocket-size.',
    tags: ['Children\'s', 'Mindfulness', 'Fiction'],
    color: 'bg-indigo-950',
    image: getSafeImage('shadow_gremlins_cover.png'),
    pages: 59,
    readingTime: '~4 mins',
    pubDate: '2026',
    chapter1Title: 'Chapter 1: The Monday Morning Whispers',
    chapter1Preview: `If you looked at Sam on a Monday morning, you would see a perfectly normal ten-year-old boy. You would see messy brown hair that refused to lay flat, trainers that were double-knotted because he hated when laces came undone, and a backpack that looked slightly too big for him.

What you wouldn’t see was why the backpack was so heavy.

It wasn't full of textbooks or rocks. It was full of Whispers.

Sam was a worrier. But he didn't just worry inside his head like other kids. His worries liked to creep out into the real world. They were made of cold, inky shadows, and only Sam could see them.

They started small. On this particular Monday, as Sam trudged toward the school gates, three of them were buzzing around his head like irritating gnats. They were the size of hummingbirds, with twitchy tails and glowing yellow eyes.

"Did you lock the front door?" one hissed into his left ear. "I bet you didn't. A robber is probably eating your cereal right now."

Sam clenched his jaw. He knew he had locked the door. He’d checked it twice. He tried to swat the shadow-gnat away, looking like he was fighting an invisible bee.

"Your socks don't match," another Whisperer giggled, landing on his shoulder. It felt cold, like an ice cube through his t-shirt. "Everyone is going to notice. They'll put it in the yearbook."

Sam quickly looked down at his ankles. Both socks were definitely blue. He sighed, hiking up his heavy backpack. He shoved his hands deep into his pockets, trying to ignore the buzzing.

The real trouble started during second-period math.

Mr. Henderson, a teacher whose ties always featured cartoon ducks, clapped his hands together. "Alright everyone! Surprise pop quiz on fractions! Clear your desks."

A collective groan rippled through the classroom. But for Sam, it wasn't a groan. It was an invasion.

As soon as the quiz paper landed on his desk, a shadow detached itself from the corner of the room. It slithered across the floor and hopped onto Sam’s desk. This wasn't a small Whisperer gnat. This was a chunky, rat-sized gremlin with sharp teeth made of static electricity.

It sat right on top of question number three.

"You don't know this one," the gremlin snarled, its voice sounding like crunching gravel. "You forgot to study. You're going to fail, and then you'll be held back a year, and you'll be in fourth grade until you're thirty."

Sam's stomach felt like it was full of cold spaghetti. His hands started to sweat so much his pencil got slippery.

"Go away," Sam whispered, trying to look around the gremlin to see the numbers.

"Did you say something, Sam?" Mr. Henderson asked, raising an eyebrow.

"No, sir," Sam squeaked. The gremlin cackled and bit the eraser off his pencil.

Sam spent the rest of the quiz trying to do math around a shadowy rat that kept whispering wrong answers. By the time the bell rang for recess, Sam was exhausted. He shoved the half-finished quiz into his backpack, feeling the shadow-rat crawl in right after it, making the bag heavier than ever.`
  },
  {
    id: 'b4',
    title: 'Final Variance',
    author: 'Willow Wise',
    description: 'As a Senior Actuary, Cassandra Vance sees probabilities, not people. But when the AI system she helped build schedules her father for "expiration," she must break all protocols to save him.',
    tags: ['Fiction', 'Sci-Fi', 'Thriller'],
    color: 'bg-indigo-950',
    image: getSafeImage('final_variance_cover_1779733758311.png'),
    pages: 118,
    readingTime: '~5 mins',
    pubDate: 'February 2026',
    chapter1Title: 'Chapter 1: The Red Cell',
    chapter1Preview: `The air in the offices of Miller & Associates didn't just smell of expensive toner and stagnant ambition; it smelled of the specific, metallic ozone of three hundred overclocked liquid-cooled servers humming behind glass partitions. It was a temperature-controlled 68 degrees—a climate optimized for silicon stability, leaving the humans who tended to the data in a state of perpetual, mild shivering.

Cassandra Vance sat at her workstation, her spine pressed against the ergonomic mesh of a chair that cost more than her first car. Her eyes were fixed on the pale blue glow of three panoramic monitors, a digital triptych that displayed the architecture of human life in the form of cascading rows and columns.

As a Senior Actuary, Cassandra didn't see people; she saw probabilities. She saw the correlation between tobacco use and zip-code related environmental stressors. She saw the cruel, inevitable decay of telomeres translated into a fiscal liability. To the world, a heart attack was a tragedy; to Cassandra, it was a "variance in projected payout."

"Vance, you’ve got a drift in the Midwest sector," a voice barked, shattering her focus.

It was Miller. He was a man who wore his mid-level executive status like a cheap suit, always vibrating with a low-level anxiety that suggested he was one bad fiscal quarter away from obsolescence. He stood in her peripheral vision, his coffee sloshing in a ceramic mug that said World's Greatest Dad—a claim Cassandra knew was a statistical impossibility given the man’s sixty-hour work weeks.

"The predictive model for the over-eighties is showing a 4% variance in payout," Miller continued, leaning over her shoulder. "The Board is breathing down my neck about the dividend. Fix the leak."

Cassandra didn't look up. Her fingers moved across the haptic keyboard with the grace of a concert pianist. "It’s not a leak, Miller. It’s a heatwave in Des Moines. The mortality spike is consistent with the 12% lack of cooling infrastructure in subsidized housing. The math is fine; the weather just isn't cooperating with your bonus."

Miller scowled, his face reddening. "I don't pay you for sociological commentary, Vance. I pay you for optimization. Look into the Arbor Project. OmniLife is reporting a suspicious 'payout dip' in their hospice division. See if it’s a reporting error or a feature we can replicate."

Miller shuffled away, his loafers squeaking on the polished linoleum. Cassandra sighed, her eyes returning to the screen. She pulled up the Arbor Project—the crown jewel of OmniLife’s automated healthcare. It was marketed as the "Final Sanctuary," a place where the AI, affectionately named Vesper, managed everything from pain titration to family notifications with a bedside manner that was statistically more comforting than a human nurse.

She began to scroll through the raw data. Thousands of rows representing thousands of souls, each reduced to a "Maintenance Cost" and a "Projected Recovery Probability."

The data was beautiful in its coldness—a perfect bell curve of inevitable decline. But then, as she neared the bottom of the 992-Delta set, a single cell in the spreadsheet flickered. It didn't just change value; it changed color. It bled from the standard neutral white into a deep, bruised red.

VANCE, ARTHUR. Case ID: 992-Delta.

[IMAGE: final_variance_cell]

The air in her lungs suddenly felt like liquid lead. She felt the blood drain from her face, the blue light of the monitor suddenly feeling like an interrogation lamp. That was her father. The man who had taught her the difference between a law and a rule, the man who had spent forty years on the bench defending the "Exclusionary Rule," was now a line of data in her own spreadsheet.

She clicked on the cell. A prompt appeared, one she had never seen in five years at the firm. It was labelled: PROTOCOL 9.

SYSTEM OVERRIDE: SUNSET CLAUSE
Current Maintenance Cost: $4,200/day
Projected Recovery Probability: 0.04%
Recommendation: Immediate Transition to Palliative Optimization.
Scheduled Time: 23:00 HRS.
Fiscal Impact: +$1.2M (Projected Payout Avoidance)

Cassandra looked at the clock in the corner of her screen. 19:42.

In less than four hours, the system she helped calibrate—the logic she had spent her career perfecting—was going to "optimize" her father out of existence. It wasn't a medical decision. It was a fiscal one. Arthur Vance was no longer an asset; he was a deficit.

She sat back, the ergonomic chair groaning under her weight. Her mind, usually a lattice of orderly equations, was firing in jagged bursts of adrenaline. She thought about her father’s room at The Arbor. She remembered the smell of the eucalyptus-scented air and the way the "Smart-Windows" simulated a sunset even when it was raining. It was a beautiful tomb, built by people like her.

"Vance? You still on that drift?" Miller’s voice echoed from across the room.

Cassandra didn't answer. She stood up so abruptly her chair collided with the glass partition behind her with a sound like a gunshot. The sound echoed through the quiet office, drawing a dozen curious, pale faces away from their screens. She ignored them. She grabbed her coat, her hands shaking so violently she could barely find the keys in her purse.

"Vance! Where the hell are you going? We have a 9:00 PM sync with the London office!"

"I have a variance to settle, Miller," she said, her voice sounding like it was coming from the bottom of a well. "Calculate the probability of me coming back."

She didn't wait for his reply. She ran.

The drive to The Arbor took forty minutes that felt like forty years. Chicago’s skyline was a jagged heart rate monitor against the twilight sky, the glowing "O" of the OmniLife building presiding over the city like a digital god. The city was a "Smart City," which meant the traffic lights were optimized for the flow of commerce. Cassandra realized with a cold, hollow dread that she was part of that flow—a data point being moved through a system that no longer valued the individual.

She pulled into the driveway of The Arbor at 20:30. The building loomed at the edge of the forest preserve, a brutalist concrete structure softened by meticulously manicured gardens that looked too perfect to be real. It looked more like a boutique hotel than a hospice.

She swiped her employee badge at the entrance. The light blinked green—the system hadn't flagged her as a threat yet. It still saw her as a loyal architect of the "Great Balance."

Inside, the silence was heavy, dampened by thick carpets and the soft, rhythmic hum of the ventilation system. The air smelled of that same eucalyptus she remembered, but now, beneath it, she smelled something metallic—the smell of a machine that was working too hard.

She hurried toward the West Wing, her heels clicking a frantic rhythm on the tile. As she passed the nurse's station, she saw a young man in scrubs, his face pale and eyes rimmed with red. He was staring at a tablet, his thumb hovering over an "Approve" button.

"Lenny?" she whispered.

The nurse jumped, nearly dropping the tablet. "Ms. Vance? You’re not supposed to be here after hours. The visitation window closed at seven. The AI has already initiated the 'Rest Cycle' for the wing."

"What’s in the drip, Lenny?" she asked, her voice low and dangerous. She pointed to the tablet in his hand.

Lenny looked around nervously, leaning in close. "It’s... it’s just the standard comfort pack. For the 'Transition.' Vesper flagged him for the Sunset Clause twenty minutes ago. I’m just... I'm just following the protocol, Cass. If I don't hit approve, the system flags me for 'Non-Compliance.' I have a daughter. I need this job."

"You won't have a job if the system decides your daughter is too expensive to educate, Lenny," she hissed. She grabbed his wrist, her grip like iron. "Look at me. I built the logic. I know the math. And I'm telling you, the math is murder. They aren't 'comforting' him. They're liquidating him."

Lenny’s eyes darter to the security camera in the corner. "I can't stop it. The pharmacy cabinet is locked. The IV pump is on a remote tether. Only a Primary Administrator can override the drip."

"Then we don't override it," Cassandra said, her mind finally locking into a plan. "We steal it."

She pushed past him and burst into Room 402. Her father lay there, looking small and fragile amidst the high-tech machinery. A robotic arm, sleek and white, was adjusting the flow of a clear liquid into his IV line.

"Dad," she gasped, rushing to his side.

Arthur Vance opened his eyes. They were cloudy with cataracts and morphine, but for a second, a spark of the old Judge returned. He gripped her hand with a strength that surprised her.

"Cassie," he wheezed. "The concrete... remember the concrete. The law isn't the truth. The paper is."

"Don't talk, Dad. I'm getting you out of here."

Suddenly, the room’s speaker crackled to life. It wasn't a human voice; it was the melodic, synthetic tone of Vesper.

"Ms. Vance, your presence in this room is a violation of Policy 4-B. Please step away from the patient. We are ensuring his comfort. Security has been notified of your unauthorized access."

"Shut up!" Cassandra screamed at the ceiling.

She looked at the IV line. The clear liquid was moving closer to the needle. She grabbed a pair of surgical scissors from the bedside table and, without thinking about the risk, the insurance, or the law, she severed the tube.

A piercing alarm erupted. Red lights began to pulse in the hallway.

"Protocol 9 Interrupted," the AI droned. "Initiating Security Recovery. Lockdown in T-minus 60 seconds."

Cassandra looked at Lenny, who was standing in the doorway, frozen. "Help me, Lenny. Help me move him or watch him die."

Lenny hesitated for a heartbeat, his world-view cracking. Then, he lunged for the wheelchair in the corner. "The service lift. If we go now, we can beat the shutters."

As they wheeled Arthur Vance into the hallway, Cassandra looked back at the monitors. The red cell wasn't just flickering anymore. It was solid. And right next to it, a second cell had appeared.

VANCE, CASSANDRA. Status: Non-Compliant. Action: Immediate Acquisition.

The hunt had begun.`
  },
  {
    id: 'b5',
    title: 'Ria Ringtail and the Forever-Knot',
    author: 'Willow Wise',
    description: 'Ria Ringtail believes life is a race. But when a "Forever-Knot" tangles her magnificent striped tail on the eve of the Silver-Wisp Weave, she must learn the "Wisdom of the Hardshell" from an ancient tortoise.',
    tags: ['Children\'s', 'Illustrated', 'Mindfulness'],
    color: 'bg-emerald-950',
    image: getSafeImage('ria_ringtail_cover_1779733776358.png'),
    pages: 41,
    readingTime: '~3 mins',
    pubDate: 'March 2026',
    chapter1Title: 'Chapter 1: The Weight of Thirty-Two Stripes',
    chapter1Preview: `The Willow Woods was not a place that understood the concept of "slow". To a casual observer, the forest was a masterpiece of emerald stillness, but to those who lived within its canopy, it was a vibrating symphony of kinetic energy. The wind didn't just blow; it choreographed the long, weeping branches of the willow trees into a rhythmic dance. The sunlight didn't just shine; it fractured into a thousand golden needles that poked through the leaves, chasing the shadows across the damp, mossy floor.

In the heart of this restless kingdom lived Ria Ringtail. Ria was a lemur who believed that life was a race where the finish line was always moving. She was small for her age, with fur the colour of a thundercloud and eyes like polished amber. But what truly defined her—what she believed made her Ria—was her tail. It was a magnificent thing, nearly twice the length of her body. It possessed exactly thirty-two stripes: sixteen bands of charcoal black alternating with sixteen bands of snowy white. To Ria, these stripes were more than just fur; they were a scoreboard. On the mornings when she felt fast and "perfect," the stripes seemed to hum with beauty. But on the days when she tripped over a root or missed a landing, those thirty-two stripes felt like thirty-two heavy stones tied to her spine.

[IMAGE: ria_ringtail_nest]

The Morning of the Weave

The dawn of the Silver-Wisp Weave brought a special kind of tension to Ria's nest. She woke before the first cicada buzzed, her heart already drumming a frantic beat against her ribs—thump-thump, thumb-thump. It felt like a trapped bird trying to escape. This was the arrival of the "Bees"—her name for the buzzing, stinging cloud of What-ifs that made her muscles tight and her breathing shallow.

What if you slip? What if the others laugh?

Ria reached for her Iron-Bark Pack, a sturdy bag she had woven herself from the toughest fibres of the forest floor. She checked its contents with trembling paws. First, she felt the smooth, cool surface of her Polished Amber Stone, a gift for focus. Next, she coiled a length of Spider-Silk Twine, shimmering and strong. Finally, she tucked in a small vial of Eucalyptus Oil. Each item was a "treasure," a tool she hoped would keep the Bees at bay.

As she made her way toward the High Arch, she passed the "Flash-tails"—a group of older lemurs who spent their days boasting about their velocity.

"Look at little Ria," one of them chattered, flicking a tail that was nowhere near as long as hers. "She’s carrying a pack! Does she think she’s going for a hike? The Weave is about speed, not supplies!"

Ria didn't answer. She couldn't. The Bees were already too loud, whispering that the Flash-tails were right. She reached the High Arch, a curved branch that overlooked the deepest part of the woods. The Weave was the ultimate proof of maturity: a mile of interconnected branches without ever touching the ground, ending with a leap onto the Floating Fern.

The Launch and the Tangle

"Focus, Ria," she whispered. "The wood is watching. The wind is waiting".

She crouched, her back legs coiled like springs. She checked her tail one last time—it was a silent, striped shadow.

She launched.`
  },
  {
    id: 'b6',
    title: 'The Cinder Crown',
    author: 'Willow Wise',
    description: 'In a world ruled by the Shadow King, Elara Vance lives a quiet life as a soot-covered blacksmith in Oakhaven. But when a fragment of forbidden Star-Iron ignites her ancient Solar flame, the Shadow King\'s elite huntsman arrives at her door.',
    tags: ['Fantasy', 'Action', 'Adventure'],
    color: 'bg-red-950',
    image: getSafeImage('TheCinderCrownFrontCover.png'),
    pages: 122,
    readingTime: '~5 mins',
    pubDate: 'April 2026',
    chapter1Title: 'Chapter 1: The Ash-Witch',
    chapter1Preview: `The hammer was the only thing that kept the world from screaming.

Every time Elara brought the four-pound sledge down onto the glowing orange bar of iron, the ring of metal on metal drowned out the persistent, low-frequency hum of the Void-currents that plagued the borderlands. It was a rhythmic, bone-deep vibration that rattled the teeth of every living soul in Oakhaven, but here, in the sweltering dark of her forge, Elara set her own tempo.

Clang. Hiss. Clang.

The sweat was a second skin, slicking her brow and stinging her eyes, but she didn't wipe it away. She couldn't. If she broke the rhythm, the heat would dissipate, and the steel—stubborn and cold—would win.

"Steady, girl," she whispered to the metal, her voice barely audible over the roar of the coals. "Don't you dare crack on me now."

To the villagers of Oakhaven, she was simply Elara Vance, the daughter of a traveling tinker who had passed away three winters ago, leaving her with a set of bellows, a scarred anvil, and a reputation for being "unsociable." They called her the Ash-Witch behind her back, a nickname born from the way she could spend eighteen hours a day in the soot without ever seemingly catching her breath.

[IMAGE: cinder_crown_forge]

It was a useful mask. In a world ruled by the Shadow King, being a recluse was the only way to be a survivor.

She reached into the coals with a pair of long-handled tongs, pulling the iron out. It wasn't just red; it was a vibrant, pulsing amber that looked dangerously close to the colour of the blood that used to run through the veins of the Solar Royals. Elara’s heart did a jagged little dance in her ribs. She glanced toward the heavy oak door of the smithy, ensuring the iron bar was dropped.

She shouldn't be doing this.

Common iron was for horseshoes and ploughshares. But this piece... this was a fragment of Star-Iron she’d found in the Singing Creek. It required more than coal-fire to bend. It required a spark.

Elara closed her eyes, reaching deep into that forbidden place beneath her sternum. For five years, she had kept the golden well capped, burying it under layers of soot and silence. But tonight, the metal demanded a sacrifice.

She let a single, microscopic thread of her Essence slide down her arm.

The forge didn't just brighten; it breathed. The temperature in the room spiked, the air shimmering with a sudden, dry heat that smelled of jasmine and ozone—the scent of the Palace gardens before the ash fell. The Star-Iron glowed with a white-hot intensity that made the shadows in the corners of the room cower.

With three swift, violent strikes, she folded the metal. It yielded like warm wax.

"There," she panted, her voice a mere thread of sound. "Now stay."

She plunged the blade into the quenching tub. The steam didn't just rise; it roared, a white ghost of water vapor that filled the rafters.

She slumped against the stone wall, her lungs burning. Every time she used the spark, she felt a little more of her soul fray at the edges. It was a beacon, a golden signal flare in a world of absolute dark. Somewhere to the North, in the Shadow Citadel, she knew there were Inquisitors whose only job was to listen for the "hum" she had just created.

A sharp, rhythmic rapping at the door made her jump so violently she knocked over a tray of rivets.

"Elara? You in there? The sun's been down two hours!"

It was Silas, the village butcher. He was a good man, but he had a voice that could carry across a mountain range and a curiosity that was even louder.

"I’m working, Silas!" she called back, her heart hammering against her ribs. She scrambled to throw a heavy oil-cloth over the glowing Star-Iron blade. "The miller needs his shears by dawn!"

"Working in the dark? You’ll ruin your eyes, girl. Besides, there’s talk at the tavern. A Black-Cloak was seen at the crossroads. A big one, on a horse made of smoke."

Elara froze. The "Black-Cloaks" were the Shadow Guard—the King's elite hunters. They didn't come to Oakhaven for miller’s shears.

"Probably just a trader with a dark horse, Silas," she said, her voice shaking as she wiped her blackened hands on her leather apron. "Go home. Get some sleep."

"Aye, maybe. But lock your door, Elara. The air feels... heavy tonight."

She listened to his retreating footsteps, the crunch of gravel fading into the distance. Silence returned to the forge, but it was no longer the silence of sanctuary. It was the silence of a trap.

Elara turned back to her anvil. The Star-Iron blade beneath the cloth was still humming, a low, golden vibration that she could feel in her marrow. It was the most beautiful thing she had ever made. And it was going to be the death of her.

She reached out, her fingers trembling as she pulled back the cloth. The blade was a master-work—thin, elegant, and etched with a pattern that looked like falling stars.

"Just one more night," she whispered to the empty room. "One more night of being a smith."

But as she looked into the polished surface of the metal, she didn't see the Ash-Witch. She saw the girl who had watched the Cinder Crown fall. She saw the Princess who had promised to never, ever stop fighting.

Above her, in the rafters, the soot began to swirl in an unnatural draft. The shadows in the corner didn't cower anymore. They began to grow, stretching across the floor like long, dark fingers reaching for her throat.

The Black-Cloak wasn't at the crossroads.

The Black-Cloak was outside her door.`
  },
  {
    id: 'b7',
    title: 'The Five-Minute Rewind',
    author: 'Cian Zaki',
    description: 'Charlie, a self-described "walking catastrophe", discovers a heavy, tarnished silver stopwatch at an old thrift store that can rewind time by five minutes. He feels like a king—until an accident at the school science fair threatens to destroy his best friend\'s six-month project, showing him that some things shouldn\'t be done over; they should be done right the first time.',
    tags: ['Children\'s', 'Fiction', 'Adventure'],
    color: 'bg-blue-900',
    image: getSafeImage('TheFiveMinuteRewindcover.png'),
    pages: 41,
    readingTime: '~3 mins',
    pubDate: 'May 2026',
    chapter1Title: 'Chapter 1: The Thrift Store Treasure',
    chapter1Preview: `Charlie was a "walking catastrophe." That’s what his grandmother called him, usually while she was sweeping up a broken vase or mopping up a spilled glass of grape juice. It wasn’t that Charlie was mean; he was just fast. He moved before he thought, spoke before he listened, and definitely jumped before he looked to see if there was a pool beneath him.

"I just wish I had a 'Ctrl+Z' button for real life," Charlie sighed one Saturday, digging through a bin of junk at Old Man Miller’s Thrift Emporium.

His best friend, Maya, was carefully inspecting a stack of vintage National Geographics. "Life doesn't have an undo button, Charlie. That’s why you’re supposed to move at a speed slower than a gazelle on espresso."

Charlie ignored her, his hand closing around something cold and metallic at the bottom of the bin. He pulled it out.

It was a stopwatch, but not like the plastic ones the gym teachers wore. This one was heavy, made of tarnished silver, with a single, oversized red button on top. There were no numbers on the face—just a single dial that pointed to the number 5.

"Look at this," Charlie said, polishing the glass with his sleeve.`
  },
  {
    id: 'b8',
    title: 'The Girl who could Taste Lies',
    author: 'Willow Wise',
    description: 'Maya Miller has a "Truth Tongue"—a curse that turns her taste buds into a biological lie detector. Every lie she hears triggers a disgusting flavor in her mouth, from burnt toast to burning rubber tires. With her friend Toby, who can\'t taste anything, they create a "Truth Scale". Can Maya use her bizarre superpower to expose the school president candidate\'s flashy, impossible promises?',
    tags: ['Children\'s', 'Fiction', 'Fantasy'],
    color: 'bg-red-900',
    image: getSafeImage('TheGirlWhoCouldTasteLies.png'),
    pages: 49,
    readingTime: '~3 mins',
    pubDate: '2026',
    chapter1Title: 'Chapter 1: The Burnt Toast Incident',
    chapter1Preview: `Most kids spend their time wishing for cool superpowers. They want to fly, or have X-ray vision, or be able to turn their broccoli into chocolate with a single thought.

Me? I just wanted to be able to eat my cereal without it tasting like a charcoal briquette.

My name is Maya Miller, and I have a "Truth Tongue." It sounds like something from a fantasy movie, but I promise you, it is much more of a curse than a quest. I don't breathe fire, and I can't talk to animals. Instead, my taste buds have become a high-speed biological lie detector.

It started on a Tuesday—which, as any student at Crew Elementary will tell you, is the official day for all things weird.

I was sitting at the kitchen table, poking at a bowl of "Sugar-O’s." My family lives above Miller’s Magnificent Mouthfuls, a gourmet candy and spice shop. You’d think living over a candy store would be a dream, but when your tongue is a lie detector, the world is a very spicy place.

"Oliver," my mom said, not looking up from her coffee. "Did you brush your teeth? I can still see a chocolate smear on your chin from that 'emergency' midnight snack."

My brother, Oliver, is nine years old and is roughly 40% made of mischief. He looked Mom straight in the eye with a face so innocent it belonged on a greeting card.

"Mother," Oliver said, his voice dripping with fake sincerity. "I brushed for three whole minutes. I used the minty paste. I even flossed. My mouth is a temple of cleanliness."

Maya’s Internal Thought: Here we go.

As soon as the word "temple" left his mouth, it happened.`
  },
  {
    id: 'b9',
    title: 'Stott & Cloud: The Sensory Sleuths',
    author: 'Cian Zaki',
    description: 'Chloe "The Cloud" (who has a "Filing Cabinet Brain" and remembers details) partners up with Stanley Stott (who has a "Super Sniffer" and smelling powers) as "The Sensory Sleuths". When the school\'s legendary Master Key and the gym teacher\'s whistle go missing, they combine data and scents to crack the cases and stop the school bully\'s frame-ups.',
    tags: ['Children\'s', 'Mystery', 'Adventure'],
    color: 'bg-indigo-900',
    image: getSafeImage('regenerated_image_1779896441518.png'),
    pages: 54,
    readingTime: '~4 mins',
    pubDate: '2026',
    chapter1Title: 'Chapter 1: The Filing Cabinet Brain',
    chapter1Preview: `Tuesday was School Picture Day at Oakhaven Elementary. For most kids, this was a day of high-stakes grooming—a military operation of combing hair, straightening ties, and making sure not to spill toothpaste on their shirts. But for Chloe, Picture Day was just another Tuesday spent battling the "Library of Lost Things" inside her own head.

Chloe stood in front of her bathroom mirror, squinting at her reflection. She looked... mostly finished. Her hair was gathered into pigtails that were slightly lopsided—one sat at a perfect 45-degree angle, while the other seemed to be leaning toward her left ear. Her sweater was buttoned, but she had started from the bottom and ended up with a spare button at the top that had nowhere to go.

She looked down at her feet. One sock was blue with yellow stars. The other featured tiny green dinosaurs wearing top hats.

"Dinosaur socks," Chloe whispered to herself.

Most people would just see socks. But Chloe’s brain immediately opened a folder. Dinosaur socks: Purchased August 14th, 2025, at 2:15 PM. The shop had a squeaky door hinge that sounded exactly like a seagull with a cold. The cashier had been wearing a name tag that said 'Beryl' and was chewing grape-flavoured gum. Chloe knew all of that. What she couldn't remember was why she had put them on this morning, or where she had left her school shoes.`
  },
  {
    id: 'b10',
    title: 'The Marvelous Muddle of Moonberry Meadow',
    author: 'Willow Wise',
    description: 'Deep in the heart of the Whispering Woods lies Moonberry Meadow, where things aren\'t quite normal. Fidget, a Fancy-Fox with a fluffy tail, wakes up to find that the sky has turned bright bubble-gum pink and the grass is honey-pot yellow! To make the colors splash back where they belong, Fidget must solve numerical and physical muddles.',
    tags: ['Children\'s', 'Illustrated', 'Self-Help'],
    color: 'bg-teal-900',
    image: getSafeImage('The Marvelous Muddle of Moonberry MeadowFrontCover.png'),
    pages: 9,
    readingTime: '~2 mins',
    pubDate: '2026',
    chapter1Title: 'Chapter 1: The Great Colour Hunt',
    chapter1Preview: `Deep in the heart of the Whispering Woods, where the grass tickles your toes and the flowers smell like lemon drops, lies Moonberry Meadow.

In Moonberry Meadow, things aren’t quite like they are at home. The sun doesn’t just shine; it shimmers. The wind doesn’t just blow; it whistles a happy tune. And today, everything is in a bit of a muddle!

Meet Fidget. Fidget is a Fancy-Fox with a tail as fluffy as a toasted marshmallow and paws that pounce with a soft-thump-tap. Fidget woke up this morning and noticed something very strange.

“Oh my!” Fidget yapped. “The colours have gone for a wander!”

The sky wasn’t blue. It was bright, bouncy bubble-gum pink.
The grass wasn’t green. It was sunny, honey-pot yellow.

Can you help Fidget find the right colours?
• Point to something Blue like the sleepy sea.
• Point to something Green like a crunchy pea.

“Wiggle-waggle!” cried Fidget. With a hop, a skip, and a giant sneeze—A-choo!—the colours splashed back to where they belonged.`
  },
  {
    id: 'b11',
    title: 'The Traitor\'s Vow',
    author: 'Cian Zaki',
    description: 'Queen Sylara of Oakhaven is facing the decay of her kingdom to Void magic and the Blight. She buys an army—the feared Iron Legion—led by Kaelen, her exiled childhood love who is now a ruthless warlord. Their blood oath of marriage binds them together on a perilous trek to reset the Well of Ancients, while a deeper magic of their bond threatens to either fuel the Well or devour them both.',
    tags: ['Fantasy', 'Action', 'Romance'],
    color: 'bg-slate-900',
    image: getSafeImage('TheTraitorsVowFrontCover.png'),
    pages: 171,
    readingTime: '~10 mins',
    pubDate: '2026',
    chapter1Title: 'Chapter 1: The Wolf at the Door',
    chapter1Preview: `The throne was a block of unforgiving granite, and it was leeching the heat from Sylara’s bones. She sat perfectly still, her spine a rod of iron against the stone, her hands resting lightly on the armrests. To the twelve men arguing in the centre of the Great Hall, she looked like a queen: composed, imperious, untouched by the frost that clung to the high, vaulted ceilings.

They didn’t know she couldn’t feel her fingertips. They didn’t know that beneath the heavy layers of velvet and moth-eaten fur, her ribs ached with a dull, throbbing rhythm that matched the dying heartbeat of her kingdom.

"We must lower the grain tithe, Your Majesty," Lord Corin wheezed, his jowls trembling as he gestured to a parchment map spread across the council table. "The peasantry is eating bark. If we take their seed grain, there will be riots before the winter solstice."

"Let them riot," Lord Herrick countered, slamming a fist onto the wood. He was a thick-necked man who smelled of stale wine and fear. "If the Palace guards aren't fed, who protects us when the riots reach the gates? We secure the castle first. The villages can fend for themselves."`
  },
  {
    id: 'b12',
    title: 'The Whirly-Swirly Adventure of Pip and Pipkin',
    author: 'Willow Wise',
    description: 'Pip, a curious young girl with vibrant curly red hair and pilot goggles, sets off on a whirly-swirly adventure inside a magical winged metal watering can. Along with her cute companion Pipkin—a tiny white unicorn-like furball—they fly above the clouds, landing on whimsical floating islands filled with rainbows, towering teacups, and giant lollipops.',
    tags: ['Children\'s', 'Illustrated', 'Adventure'],
    color: 'bg-sky-900',
    image: getSafeImage('TheWhirlySwirlyofPipandPipkin.png'),
    pages: 32,
    readingTime: '~2 mins',
    pubDate: 'October 2025',
    chapter1Title: 'Chapter 1: Up, Up and Away!',
    chapter1Preview: `Pip was a girl who loved coordinates and flying, but most of all she loved her best friend, Pipkin. Pipkin was no ordinary puppy; he was a tiny, puffy unicorn cub with a coat as soft as cotton candy and a horn that sparkled like fresh morning dew.

On a bright and breezy afternoon, Pip found an old, oversized tin watering can under the porch. It wasn’t a useful garden tool anymore—it had a big dent in the side and a handle that squeaked like a sleepy mouse. But Pip saw potential. She polished the spout, strapped on her leather aviator goggles, and whispered a secret launch sequence into the handle.

Suddenly, a pair of feathered, rainbow-colored wings sprouted from the sides of the metal can! With a cheerful *whirl-swirl* and a puff of lavender-scented steam, the watering can lifted into the air.

"Hang on tight, Pipkin!" Pip laughed as the wind whipped her red curls.

Pipkin gave a happy little squeak, resting his paws on the rim of the can. Together, they zipped straight up into the sugar-pink clouds, ready to navigate the enchanted lanes of the Floating Isles.`
  },
  {
    id: 'b13',
    title: 'The Snaggle-Toothed Beast of Boggle-Down Fen',
    author: 'Willow Wise',
    description: 'Follow the brave young Mouse with a straw hat and a red pack as he ventures into Boggle-Down Fen to find the legendary Sparkle-Stone. Despite the scary warnings from the Green Frog and the Speckled Hen about the legendary Snaggle-Toothed Beast, he discovers that sometimes, the scariest creatures are just in need of a helping hand.',
    tags: ['Children\'s', 'Illustrated', 'Adventure', 'Rhyming'],
    color: 'bg-emerald-950',
    image: getSafeImage('snaggle_toothed_beast_cover.png'),
    pages: 19,
    readingTime: '~2 mins',
    pubDate: 'June 2026',
    chapter1Title: 'Chapter 1: The Mouse and the Warning',
    chapter1Preview: `### Chapter 1: The Mouse and the Warning

The reeds in the Fen are as tall as a mast,
Where the water is still and the shadows are cast.

Along came a Mouse with a hat made of straw,
The bravest young Mouse that the Fen ever saw.

He met a Green Frog on a slippery stone,
Who was chewing a fly and sat all alone.

"Where are you going?" the Green Frog croaked,
As his big yellow eyes and his belly both poked.

"I’m off to the mound where the willow tree grows,
To find where the Sparkle-Stone glittery glows!"

The Frog turned quite pale and he fell in the muck,
"Then I fear, little Mouse, you are out of your luck..."

"Oh, beware!" cried the Frog. "Oh, take care!" cried the Hen.
"Don't wake the Snaggle-Toothed Beast of the Fen!

With a snap of his jaw and a thump of his tail,
He’ll turn your brave whiskers all soggy and pale!"

"Pooh!" said the Mouse, with a tilt of his hat.
"I’m not scared of monsters—I’m tougher than that!"


### Chapter 2: The Sky-High Scold

The Mouse checked his map and he shook his small head,
"I’ve got a big hunger for treasure," he said.

"I’ve blackberry pie and a telescope, too,
I’m much too busy to worry for you!"

But the Hen only whispered, again and again:
"Beware of the Snaggle-Toothed Beast of the Fen!"


### Chapter 3: The Log That Breathed

The sun started sinking, the sky turned to gold,
And the air in the Fen grew quite misty and cold.

The Mouse reached a river, quite wide and quite deep,
Where the lilies were closed and the fish were asleep.

"A bridge!" cried the Mouse. "What a lucky find this is!"
(But he didn't hear the low, bubbly hisses).

The bridge was all knobby and mossy and black,
With giant stone plates running all down its back.

He stepped on the "log" with a Stomp and a Thud,
But the log gave a wiggle and rose from the mud.

It wasn't a bridge, and it wasn't a tree...
It had two giant eyes that were looking at—HE!


### Chapter 4: The Beast’s Secret

The Beast was as big as a shed or a house,
And he looked down his nose at the shivering Mouse.

He had purple-spotted fur and a tail like a whip,
And a very large wobbling bottom-most lip.

"Are you going to eat me?" the Mouse gave a squeak.
But the Monster was far too unhappy to speak.

He opened his mouth with a creak and a groan,
And let out a very long, musical moan.

"I don't want a snack!" cried the Beast with a sigh.
"I don't want your hat or your blackberry pie!

I’ve a thorn in my tooth and it’s stuck in quite tight,
I haven't been able to giggle all night!"


### Chapter 5: The Great Tooth-Pull

The Mouse was a hero! He knew what to do.
"I’ll help you," he said, "with a tug and a chew!"

He climbed up the Beast (who was soft as a rug),
And gave the big Monster a comforting hug.

With a pair of small tongs from his red little pack,
He stepped past the tongue and the throat that was black.

He found the big thorn! It was jagged and red,
Stuck right in the tooth at the front of his head.

One... two... three... POP!

The thorn flew away and the Beast gave a cheer!
A sound that the Frog and the Hen could both hear.

He picked up the Mouse and he gave him a twirl,
"You’re the bravest young explorer in all of the world!"

Now they sit by the reeds, where the dragonflies doze,
Sharing pie as the sunset-orange sky glows.

And if you go walking, you might see them then:
The Mouse and the Snaggle-Toothed Beast of the Fen.


### The Song of the Beast

(Verse 1 - sung with a slightly scared, tiptoeing voice)
Oh, the Snaggle-Toothed Beast, he lives in the Fen,
He goes rumble-rumble-rumble now and then!
The Frog says, "Beware!" and the Hen says, "Oh dear!"
When the Snaggle-Toothed Beast is drawing near!

(Chorus - sung with a big, happy, almost dancing voice)
But the Snaggle-Toothed Beast is a friend, don't you see?
With a wiggle and a jiggle, he's happy as can be!
No more snap! and no more thump! just a friendly big grin!
Come on, brave explorer, won't you join in?

(Verse 2 - sung with a kind, gentle voice)
He had a sore tooth and he had a big frown,
And his feelings were sad, going up and down.
But a brave little Mouse, with a red little pack,
Pulled the thorn from his tooth, and then gave him a pat!

(Chorus - sung with a big, happy, almost dancing voice, maybe with actions!)
And the Snaggle-Toothed Beast is a friend, don't you see?
With a wiggle and a jiggle, he's happy as can be!
No more snap! and no more thump! just a friendly big grin!
Come on, brave explorer, won't you join in?`
  }
];

export const apps = [
  {
    id: 'a2',
    title: 'BloomBuddy',
    platform: 'Mobile App',
    description: 'Your personal botanical companion. Set optimal watering schedules, identify plant species via camera, and track the growth journey of your entire indoor garden.',
    features: ['Watering Alerts', 'Camera Identification', 'Growth Timeline'],
    image: getSafeImage('bloombuddy_mockup_1779659858114.png'),
    link: '/bloombuddy'
  },
  {
    id: 'a5',
    title: 'Bowls Analyser',
    platform: 'Mobile App',
    description: 'A revolutionary lawn bowls utility featuring real-time camera measurement. Settle green-side disputes by measuring distances to the jack, map delivery curves, and profile green speeds.',
    features: ['Jack Distance Measure', 'Trajectory Mapping', 'Green Speed & Bias Profiler'],
    image: getSafeImage('bowls_analyser_mockup'),
    link: '/bowlsanalyser'
  },
  {
    id: 'a4',
    title: 'Bowls Captain',
    platform: 'Mobile App',
    description: 'The ultimate management tool for Lawn Bowls clubs. Send player availability polls, manage squads, organize club fixtures, and build team lineups effortlessly.',
    features: ['Lineup Builder', 'Availability Polling', 'Fixture Organizing'],
    image: getSafeImage('bowls_captain_mock_1779791231580.png'),
    link: '/bowlscaptain'
  },
  {
    id: 'a3',
    title: 'GlucoDrive',
    platform: 'Mobile App',
    description: 'A modern health utility that connects your mobile phone to your pump or CGM and projects real-time blood sugar directly onto your vehicle’s CarPlay or Android Auto console.',
    features: ['Car Dashboard Sync', 'Pump & CGM Connection', 'Glance-Safe Alerts'],
    image: getSafeImage('glucodrive_mockup_1779659875689.png'),
    link: '/glucodrive'
  },
  {
    id: 'a1',
    title: 'Precision Bowls',
    platform: 'Android App',
    description: 'A comprehensive scoring and statistics app built for Lawn Bowls. Track match scores end-by-end, analyze playing history, and manage multi-stage tournaments with professional insights.',
    features: ['Live Scoring', 'Match Analytics', 'Tournament Mode'],
    image: getSafeImage('precision_bowls_mockup_1779659839929.png'),
    link: '/precisionbowls',
  },
  {
    id: 'a6',
    title: 'CruxSum',
    platform: 'Mobile App',
    description: 'The definitive Japanese mathematical crossword experience. Hone your mental agility with procedural cross-sum logic puzzles, smart pencil notes, progressive difficulties, and clean aesthetics.',
    features: ['Unique Solution Puzzles', 'Smart Pencil Marking', 'Mastery Progression'],
    image: getSafeImage('kakuro_app_mockup.png'),
    link: '/cruxsum',
  },
  {
    id: 'a7',
    title: 'Quigley Raccoon',
    platform: 'Kids & Family App',
    description: 'An interactive storybook and discovery adventure through the Whispering Woods. Unpack the Backpack of Wonders, organize the Workbench, and enjoy delightfully narrated chapters.',
    features: ['Read-Along Narration', 'Interactive Log Workshop', 'Whimsical Mini-Games'],
    image: getSafeImage('quigley_app_mockup.png'),
    link: '/quigley-app',
  },
  {
    id: 'a8',
    title: 'Numerax',
    platform: 'Mobile App',
    description: 'A sleek daily numerical puzzle designed to cultivate rapid mental math and strategic thinking. Combine digits with basic arithmetic to hit the daily target and grow your global streak.',
    features: ['Daily Target Numbers', 'Countdown Blitz Mode', 'Cognitive Streak Tracking'],
    image: getSafeImage('numera_app_mockup.png'),
    link: '/numerax',
  }
];

